import React from "react";
import { Provider } from "react-redux";
import { connect } from "react-redux";
import { store } from "./fraction/redux/store";
import { createTheme, ThemeProvider } from "@vitalsource/vstui/themes";
import { ocean, cheddar, magic } from "@vitalsource/vstui/colors";
import * as vstuiLocales from "@vitalsource/vstui/i18n/translations";
import classnames from "classnames";
import { colors } from "chameleonjs";
import Dispatcher from "./fraction/dispatcher";
import { Helmet } from "react-helmet";
import { selectors as booksSelectors } from "./fraction/redux/books";
import {
  actions as userActions,
  selectors as userSelectors
} from "./fraction/redux/user";
import {
  actions as licenseActions,
  selectors as licenseSelectors
} from "./fraction/redux/license";
import {
  actions as brandActions,
  selectors as brandSelectors
} from "./fraction/redux/brand";
import {
  actions as routerActions,
  selectors as routerSelectors,
  history as routerHistory
} from "./fraction/redux/router";
import { selectors as bridgeSelectors } from "./fraction/redux/bridge";
import {
  actions as localeActions,
  selectors as localeSelectors
} from "./fraction/redux/locale";
import { selectors as searchSelectors } from "./fraction/redux/search";
import {
  actions as storageActions,
  selectors as storageSelectors
} from "./fraction/redux/storage";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { toastUtils } from "./fraction/utils/toast-utils";
import CatLoader from "./fraction/ui/loader-generic";
import setTitle from "./fraction/utils/set-title";
import Routes from "./fraction/router";
import "./fraction/ebook-converter";

const fetchLocale = () => {
  const state = store.getState();
  const brandLocale = brandSelectors.getBrandValue(state, "locale", "en");
  const userLocale = userSelectors.getUser(state).locale;
  const loginBrand = storageSelectors.getKey(state, "updateLanguageOnLogin", {
    userScope: false
  });
  const isNative = routerSelectors.isNative(state);
  const queryStringLocale = routerSelectors.getQueryParam(state, "locale");
  return store.dispatch(
    localeActions.fetchLocale(
      // Fully general support for query string locale is kind of complicated once
      // you consider the infinite persistence of updateLanguageOnLogin, language
      // changes post-param consumption, re-initialization of locale on brand
      // param changes, and library/reader synchronization. This feature is only
      // needed for native webviews, so let's only enable it for that more limited
      // scenario and avoid having something broken in online
      (isNative && queryStringLocale) || loginBrand || userLocale || brandLocale
    )
  );
};

class MainHelmet extends React.Component {
  componentDidMount() {
    const favicons = document.getElementsByClassName("favicon");
    for (let i = 0; i < favicons.length; i++) {
      favicons[i].remove();
    }
  }
  render() {
    const { brand, currentLocale } = this.props;
    return (
      <Helmet>
        <html
          lang={currentLocale}
          className={classnames({
            "cover-background": brand.Graphics.background
          })}
          style={{
            backgroundImage: brand.Graphics.background
              ? `url('${brand.Graphics.background}')`
              : ""
          }}
        />
        <meta charSet="utf-8" />
        <title>{setTitle(brand)}</title>
        <link
          type="image/png"
          rel="shortcut icon"
          href={brand.Graphics.favicon}
        />
      </Helmet>
    );
  }
}

const ConnectedHelmet = connect(state => ({
  brand: brandSelectors.getBrand(state),
  currentLocale: localeSelectors.getCurrentLocale(state)
}))(MainHelmet);

class ThemeComponent extends React.Component {
  componentDidMount() {}

  render() {
    const {
      brand,
      translationsExist,
      userFetched,
      authenticated,
      userInfo,
      bridgeUserFetching,
      currentLocale
    } = this.props;
    if (brand && brand.Theme) {
      // This was happening too early
      Fraction.Brand = { ...brand }; // TODO this will eventually be removed (Fraction.Brand that is)
    }

    if (userInfo && userInfo.email) {
      // TODO this will eventually be removed (Fraction.User that is)
      Fraction.User = { ...userInfo };
      Fraction.setupContext(userInfo);
    }

    if (userFetched && translationsExist && brand && brand.Theme) {
      // TODO better way to check if a brand is loaded than brand.Theme
      const theme = {
        ...createTheme({
          breakpoint: {
            xs: "(max-width: 375px)"
          },
          color: {
            primary: colors[brand.Theme.primary] || ocean,
            secondary: colors[brand.Theme.secondary] || cheddar
          },
          typography: {
            htmlFontSize: 10
          },
          i18n: vstuiLocales[currentLocale]
        }),
        color: {
          base: colors[brand.Theme.base] || magic,
          primary: colors[brand.Theme.primary] || ocean,
          secondary: colors[brand.Theme.secondary] || cheddar // aka accent
        }
      };

      return (
        <ThemeProvider theme={theme}>
          <div
            data-theme-base="magic"
            data-theme-primary="ocean"
            data-theme-secondary="cheddar"
            style={{ display: "inline" }}
          >
            <ConnectedHelmet />
            <Routes />
          </div>
        </ThemeProvider>
      );
    } else {
      let completeCount = 0;
      if (!bridgeUserFetching) {
        completeCount++;
      }
      if (authenticated) {
        completeCount++;
      }
      if (brand && brand.Theme) {
        completeCount++;
      }
      if (translationsExist) {
        completeCount++;
      }
      return <CatLoader count={completeCount} />;
    }
  }
}

// isLocaleFetching IS being used to trigger locales update (BO-5185)
const mapStateToProps = (state, props) => ({
  userFetched: userSelectors.isUserFetched(state),
  authenticated: userSelectors.isAuthenticated(state),
  userInfo: userSelectors.getUser(state),
  brand: brandSelectors.getBrand(state),
  translationsExist: localeSelectors.translationsExist(state),
  isLocaleFetching: localeSelectors.isLocaleFetching(state),
  currentLocale: localeSelectors.getCurrentLocale(state),
  bridgeUserFetching: bridgeSelectors.isUserFetching(state),
  query: searchSelectors.getCurrentQuery(state)
});

const mapDispatchToProps = (dispatch, props) => ({});

const ConnectedApp = connect(
  mapStateToProps,
  mapDispatchToProps
)(ThemeComponent);

class App extends React.Component {
  constructor(props) {
    super(props);
    this.state = {};
  }

  initialize = async () => {
    await store.dispatch(
      routerActions.updateLocation(routerHistory.action, window.location)
    );

    const brandKey =
      routerSelectors.getQueryParam(store.getState(), "brand") ||
      window.location.hostname;

    await Promise.all([
      store.dispatch(brandActions.fetchBrand(brandKey)),
      store.dispatch(userActions.loadUser())
    ]);

    fetchLocale();

    this.predicateSteps();
    this.listenToHistory();
  };

  updateStoredLocale = () => {
    // Update a stored locale on login
    store.dispatch({
      predicate: (state, action) =>
        userSelectors.isAuthenticated(state) &&
        storageSelectors.getKey(state, "updateLanguageOnLogin", {
          userScope: false
        }) &&
        // This param check is only strictly needed for testing native via online.
        // Real native shouldn't ever set this flag.
        !routerSelectors.isNative(state),
      action: (dispatch, getState) => {
        const state = getState();
        const localeToUpdateTo = storageSelectors.getKey(
          state,
          "updateLanguageOnLogin",
          { userScope: false }
        );
        dispatch(
          storageActions.deleteKey("updateLanguageOnLogin", {
            userScope: false
          })
        );
        dispatch(userActions.setLocale(localeToUpdateTo));
      }
    });
  };

  isRedirectToAdmin = state => {
    if (routerSelectors.isNative(state)) {
      return false;
    } else {
      return (
        bridgeSelectors.getUser(state).hasAdmin &&
        (routerSelectors.routeContains(state, "/admin") ||
          (!storageSelectors.getKey(state, "hasBeenRedirectedToBridgeAdmin", {
            sessionScope: true,
            userScope: false
          }) &&
            !userSelectors.isSsoSession(state))) &&
        brandSelectors.getBrandValue(state, "Rostering.enabled", false) &&
        !routerSelectors.routeContains(state, "/books/") &&
        !routerSelectors.routeContains(state, "/user/")
      );
    }
  };

  isRedirectToExplore = state => {
    return (
      bridgeSelectors.hasSubscriptions(state) &&
      !booksSelectors.isLoading(state) &&
      (routerSelectors.routeContains(state, "/explore") ||
        (booksSelectors.getTotalBooks(state) === 0 &&
          brandSelectors.getBrandValue(
            state,
            "Library.redirectToExplore",
            false
          ))) &&
      !routerSelectors.routeContains(state, "/books/") &&
      !routerSelectors.routeContains(state, "/user/")
    );
  };

  redirectToInitialRoute = () => {
    store.dispatch({
      predicate: (state, action) =>
        this.isRedirectToAdmin(state) || this.isRedirectToExplore(state),
      action: (dispatch, getState) => {
        const state = getState();
        if (this.isRedirectToAdmin(state)) {
          if (userSelectors.isSsoSession(state)) {
            dispatch(
              storageActions.setKey("hasBeenRedirectedToBridgeAdmin", true, {
                sessionScope: true
              })
            );
            dispatch(routerActions.push("admin"));
          }
        } else if (this.isRedirectToExplore(state)) {
          const subRoute = routerSelectors.getPathDetails(state, "explore");
          dispatch(routerActions.push("explore" + subRoute));
        }
      },
      persist: false
    });
  };

  updateLicense = () => {
    store.dispatch({
      // logged in, needs book list update, not in a book
      predicate: (state, action) =>
        userSelectors.isAuthenticated(state) &&
        userSelectors.getUser(state).needsBookListUpdate &&
        routerSelectors.getCurrentRoute(state).hash.indexOf("/books/") === -1 &&
        routerSelectors.getCurrentRoute(state).hash.indexOf("/user/") === -1 &&
        !licenseSelectors.isLicenseUpdating(state),
      action: (dispatch, getState) => {
        dispatch(licenseActions.updateLicense());
      },
      persist: true
    });
  };

  // Predicate steps are generally post login things that need to happen.
  predicateSteps() {
    this.updateStoredLocale();
    this.updateLicense();
    this.redirectToInitialRoute();

    store.dispatch({
      // logged in, needs book list update, not in a book
      predicate: (state, action) =>
        userSelectors.requiresEmailVerification(state) &&
        userSelectors.emailIsVerified(state) &&
        routerSelectors.getCurrentRoute(state).hash.indexOf("/verify") !== -1,
      action: (dispatch, getState) => {
        toastUtils.notify({
          id: "email-verified-toast",
          children: (
            <span>
              <b>{i18n.t("email-verification.account-confirmed", {})}</b>
            </span>
          ),
          dismissible: true,
          maxWidth: 250,
          horizontalPosition: "right",
          type: "success"
        });
      }
    });
    // Update License if necessary (only in library)
    /*
    // Show onboarding
    store.dispatch({
      predicate: (state, action) => userSelectors.isAuthenticated(state) && !storageSelectors.getKey("hasSeenOnboarding"),
      action: {
        type: ONBOARDING_SHOW,
      }
    })
    */
    /*
    // Auto Redeem
    const code = routerSelectors.getQueryParam(store.getState(), "auto_redeem")
    store.dispatch({
      predicate: (state, action) => userSelectors.isAuthenticated(state) && !storageSelectors.getKey("hasSeenOnboarding"),
      action: () => (dispatch, getState) => dispatch(licenseActions.redeemCode(code))
    })
    */
  }

  listenToHistory() {
    this.unlistenToHistory = routerHistory.listen(async (location, action) => {
      // Update Certain things in the state?
      await store.dispatch(routerActions.updateLocation(action, location));

      // Note: the actions of brands and locales will not refetch the current brand or locale
      const brandKey =
        routerSelectors.getQueryParam(store.getState(), "brand") ||
        window.location.hostname; // should really pull from the parameter
      await store.dispatch(brandActions.fetchBrand(brandKey));

      fetchLocale();
    });

    // Yeah... it's ugly, but because we unlisten to the history, we need to make sure we update it when we re-listen
    // This should also maintain our parameters from navigating between non-redux urls.
    /*store.dispatch(
      routerActions.push(window.location.hash.slice(2).split("?")[0])
    );*/

    // looks like we just need to send our own update to the router actions.
    store.dispatch(routerActions.updateLocation("POP", window.location));
  }

  componentDidMount() {
    this.initialize();

    // TODO remove this once we get off of the old dispatcher
    //  and move over /terms and /user/decide etc
    Dispatcher.bind(Dispatcher.USER_CHANGE, user =>
      store.dispatch(userActions.userLoaded(user))
    );
    /*
      - Set Document Title
      - Check Any Necessary Redirects
      - EULA/Terms
      - Complete Account
      - Check password strength
      - Check if they're Phony
      - Setup GA
    */
  }

  componentWillUnmount() {
    // TODO remove this once we get off of the old dispatcher
    //  and move over /terms and /user/decide etc
    Dispatcher.unbind(Dispatcher.USER_CHANGE, user =>
      store.dispatch(userActions.userLoaded(user))
    );
    if (this.unlistenToHistory) {
      this.unlistenToHistory();
    }
  }

  render() {
    return (
      <Provider store={store}>
        <ConnectedApp />
        <ToastContainer
          position="top-right"
          autoClose={false}
          newestOnTop
          closeOnClick={false}
          draggable={false}
          pauseOnFocusLoss={false}
        />
      </Provider>
    );
  }
}
export default App;
